import {
	Autocomplete,
	Box,
	IconButton,
	MenuItem,
	TextField,
	Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import RefreshIcon from "@mui/icons-material/Refresh";
import type { Moment } from "moment";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import type {
	BilletWorkOrdersPickerBilletSize,
	BilletWorkOrdersPickerCrew,
	BilletWorkOrdersPickerProductSize,
	BilletWorkOrdersPickerProductType,
	BilletWorkOrdersPickerWorkOrder,
} from "../models/BilletWorkOrders";

interface Props {
	start: Moment;
	end: Moment;
	onStartChange: (date: Moment | null) => void;
	onEndChange: (date: Moment | null) => void;
	workOrderId: number | null;
	billetSizeId: number | null;
	crewId: string | null;
	productSizeId: number | null;
	productTypeId: number | null;
	onWorkOrderChange: (id: number | null) => void;
	onBilletSizeChange: (id: number | null) => void;
	onCrewChange: (id: string | null) => void;
	onProductSizeChange: (id: number | null) => void;
	onProductTypeChange: (id: number | null) => void;
	billetSizes: BilletWorkOrdersPickerBilletSize[];
	crews: BilletWorkOrdersPickerCrew[];
	productSizes: BilletWorkOrdersPickerProductSize[];
	productTypes: BilletWorkOrdersPickerProductType[];
	workOrders: BilletWorkOrdersPickerWorkOrder[];
	onRefresh: () => void;
	onExportExcel: () => void;
	isLoading: boolean;
}

export const BilletWorkOrdersToolbar = ({
	start,
	end,
	onStartChange,
	onEndChange,
	workOrderId,
	billetSizeId,
	crewId,
	productSizeId,
	productTypeId,
	onWorkOrderChange,
	onBilletSizeChange,
	onCrewChange,
	onProductSizeChange,
	onProductTypeChange,
	billetSizes,
	crews,
	productSizes,
	productTypes,
	workOrders,
	onRefresh,
	onExportExcel,
	isLoading,
}: Props) => {
	const filteredSizes =
		productTypeId == null
			? productSizes
			: productSizes.filter((s) => s.ProductTypeId === productTypeId);

	const selectedWorkOrder =
		workOrderId == null
			? null
			: (workOrders.find((w) => w.Id === workOrderId) ?? null);

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				px: 1.5,
				py: 0.75,
				flexWrap: "wrap",
			}}
		>
			<Box sx={{ minWidth: 150, maxWidth: 170 }}>
				<DatePicker
					label="Start"
					format="MM/DD/YYYY"
					value={start}
					maxDate={end}
					onChange={onStartChange}
					slotProps={{ textField: { size: "small", fullWidth: true } }}
				/>
			</Box>
			<Box sx={{ minWidth: 150, maxWidth: 170 }}>
				<DatePicker
					label="End"
					format="MM/DD/YYYY"
					value={end}
					minDate={start}
					onChange={onEndChange}
					slotProps={{ textField: { size: "small", fullWidth: true } }}
				/>
			</Box>

			<Box sx={{ width: 200 }}>
				<Autocomplete
					size="small"
					options={workOrders}
					getOptionLabel={(o) => o.Name}
					value={selectedWorkOrder}
					onChange={(_, newValue) => onWorkOrderChange(newValue?.Id ?? null)}
					isOptionEqualToValue={(opt, val) => opt.Id === val.Id}
					renderInput={(params) => <TextField {...params} label="Work Order" />}
				/>
			</Box>

			<Box sx={{ width: 160 }}>
				<TextField
					select
					size="small"
					label="Product Type"
					fullWidth
					value={productTypeId == null ? "" : String(productTypeId)}
					onChange={(e) => {
						const v = e.target.value;
						const nextTypeId = v === "" ? null : Number(v);
						onProductTypeChange(nextTypeId);
						if (productSizeId != null) {
							const stillValid = productSizes.some(
								(s) =>
									s.Id === productSizeId &&
									(nextTypeId == null || s.ProductTypeId === nextTypeId),
							);
							if (!stillValid) onProductSizeChange(null);
						}
					}}
				>
					<MenuItem value="">All</MenuItem>
					{productTypes.map((pt) => (
						<MenuItem key={pt.Id} value={String(pt.Id)}>
							{pt.Name}
						</MenuItem>
					))}
				</TextField>
			</Box>

			<Box sx={{ width: 200 }}>
				<TextField
					select
					size="small"
					label="Product"
					fullWidth
					value={productSizeId == null ? "" : String(productSizeId)}
					onChange={(e) => {
						const v = e.target.value;
						onProductSizeChange(v === "" ? null : Number(v));
					}}
				>
					<MenuItem value="">All</MenuItem>
					{filteredSizes.map((ps) => (
						<MenuItem key={ps.Id} value={String(ps.Id)}>
							{ps.Name}
						</MenuItem>
					))}
				</TextField>
			</Box>

			<Box sx={{ width: 90 }}>
				<TextField
					select
					size="small"
					label="Shift"
					fullWidth
					value={crewId ?? ""}
					onChange={(e) => {
						const v = e.target.value;
						onCrewChange(v === "" ? null : v);
					}}
				>
					<MenuItem value="">All</MenuItem>
					{crews.map((c) => (
						<MenuItem key={c.Id} value={c.Id}>
							{c.Id}
						</MenuItem>
					))}
				</TextField>
			</Box>

			<Box sx={{ width: 130 }}>
				<TextField
					select
					size="small"
					label="Billet Size"
					fullWidth
					value={billetSizeId == null ? "" : String(billetSizeId)}
					onChange={(e) => {
						const v = e.target.value;
						onBilletSizeChange(v === "" ? null : Number(v));
					}}
				>
					<MenuItem value="">All</MenuItem>
					{billetSizes.map((bs) => (
						<MenuItem key={bs.Id} value={String(bs.Id)}>
							{`${bs.Height}x${bs.Width}`}
						</MenuItem>
					))}
				</TextField>
			</Box>

			<Tooltip title="Refresh">
				<span>
					<IconButton
						onClick={onRefresh}
						disabled={isLoading}
						size="small"
						sx={{
							bgcolor: "primary.main",
							color: "white",
							width: 28,
							height: 28,
							"&:hover": { bgcolor: "primary.dark" },
							"&.Mui-disabled": {
								bgcolor: "action.disabledBackground",
								color: "action.disabled",
							},
						}}
					>
						<RefreshIcon sx={{ fontSize: 18 }} />
					</IconButton>
				</span>
			</Tooltip>

			<Box sx={{ flexGrow: 1, minWidth: 8 }} />

			<Box sx={{ width: 200 }}>
				<ExcelButton handleExportToExcel={onExportExcel} />
			</Box>
		</Box>
	);
};
